import React, { Component } from 'react';
import NavigationUtil from '../../../util/NavigationUtil';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { TransactionData } from '../../../domain/stream/transaction/TransactionDomains';
import CustomTable from '../../custom/CustomTable';
import { ColumnProps } from 'primereact/column';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import moment from 'moment/moment';
import CustomPaginator, { CustomPaginatorProps } from '../../custom/CustomPaginator';
import CaseService from '../../../service/CaseService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import Icon from '../../../icons/Icon';
import { CaseLogExportJob } from '../../../domain/job/JobDomain';
import MessageUtil from '../../../util/MessageUtil';
import JobService from '../../../service/JobService';
import LanguageUtil from '../../../util/LanguageUtil';
import CaseEventTextUtil from '../../../util/CaseEventTextUtil';
import CollectionNames from '../../../constant/CollectionNames';
import { User } from '../../../domain/rbac/RbacDomains';
import { CaseEventLog } from '../../../domain/case/CaseDomains';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import SelectQuery = query.SelectQuery;
import SubscriptionContext = subscribe.SubscriptionContext;
import Computation = subscribe.Computation;

class CaseAuditComponent extends Component<Props, State> {
  public state: State = new State();

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly caseService: CaseService = ServiceContainer.getInstance().get(ServiceNames.CASE);
  private jobService: JobService = ServiceContainer.getInstance().get(ServiceNames.JOB);

  private computation: Computation | undefined;
  private readonly subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);

  public render() {
    const { logs, rows, currentPage, totalRecord, loading } = this.state;
    return (
      <div className={'h-full w-full p-5 max-h-[calc(100vh-20rem)] overflow-y-scroll'}>
        <div className={'flex min-h-[24px] justify-end bg-white items-center pb-2 w-full'}>
          <a className={`flex hover:fill-primary items-center justify-center relative group`} onClick={this.onExport} data-testid='audit_export_toggle_button'>
            <Icon icon={'export'} size={20} color={'#4F4F4F'} className={'cursor-pointer'} />
            <div
              className={'z-50 -bottom-5 rounded absolute hidden px-[10px] py-[2px] text-2xs text-white bg-primary group-hover:flex'}
              data-testid='audit_export_toggle_button_text'
            >
              {LanguageUtil.get({ id: 'common.label.export', defaultMessage: 'Export' })}
            </div>
          </a>
        </div>
        <CustomTable columns={this.getColumns()} value={logs} />
        <CustomPaginator
          currentPage={currentPage}
          key='case-audit'
          rows={rows}
          totalRecords={totalRecord}
          loading={loading}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          onPageChange={this.onPageChange}
        />
      </div>
    );
  }

  public componentDidMount(): void {
    this.startTracker();
    this.loadUsers();
  }

  public componentWillUnmount() {
    this.stopTracker();
  }

  private startTracker = () => {
    const collections = this.subscriptionContext.getSubscriptionsByCollection(this.getEndpoint());
    this.computation = subscribe.Tracker.autorun(this.tracker, collections);
  };

  private tracker = () => {
    this.fetchCount();
    this.buildCommentLog();
  };

  private stopTracker = () => {
    this.computation?.stop();
  };

  private onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ currentPage: event.page, rows: event.rows }, this.buildCommentLog);
  };

  private buildCommentLog = async () => {
    const logs = await this.repository.findMany(this.getQuery()) as CaseEventRecord[];
    this.setState({ logs, loading: false });
  };

  private loadUsers = async () => {
    try {
      const usersQuery = QueryBuilder.select().select('username', 'firstName', 'lastName').from(CollectionNames.USERS).build();
      const users: User[] = await this.repository.findMany(usersQuery);
      const usernameToFullName = users.reduce<Record<string, string>>((acc, u) => {
        const firstname = u.firstName ?? '';
        const lastname = u.lastName ?? '';
        const fullName = `${firstname} ${lastname}`.trim();
        acc[u.username] = fullName.length > 0 ? fullName : u.username;
        return acc;
      }, {});
      const userIdToFullName = users.reduce<Record<string, string>>((acc, u) => {
        const firstname = u.firstName ?? '';
        const lastname = u.lastName ?? '';
        const fullName = `${firstname} ${lastname}`.trim();
        if (u.id) acc[u.id] = fullName.length > 0 ? fullName : (u.username ?? '');
        return acc;
      }, {});
      this.setState((prevState) => ({ ...prevState, usernameToFullName, userIdToFullName }));
    } catch (e) {
      console.error(e);
    }
  };

  private getEndpoint = () => {
    return `case_event_log_${NavigationUtil.getTenant()}_*`;
  };

  private getQuery = () => {
    const { transaction } = this.props;
    const { rows, currentPage } = this.state;
    const endpoint = this.getEndpoint();
    return QueryBuilder.select()
      .from(endpoint)
      .limit(rows)
      .skip((currentPage - 1) * rows)
      .where(eq('transactionId', transaction?.id))
      .orderBy({
        field: 'timestamp',
        operator: 'DESC',
      })
      .build();
  };

  private fetchCount = async () => {
    try {
      const _query = this.getQuery();
      const query: SelectQuery = {
        ..._query,
        order: undefined,
        group: undefined,
        fields: [],
        top: undefined,
        offset: undefined,
      };
      const count = await this.caseService.getCaseLogCount(this.getEndpoint(), query);
      this.setState({ totalRecord: count });
    } catch (e) {
      console.error(e);
    }
  };

  private onExport = async () => {
    const query = this.getQuery();
    const job: CaseLogExportJob = {
      type: 'CASE_LOG_EXPORT',
      timestamp: Date.now(),
      status: 'PENDING',
      query,
      format: 'csv',
      extension: 'csv',
      path: '',
    };
    try {
      await this.jobService.schedule(job);
      MessageUtil.success({
        summary: 'Success',
        detail: 'Export job has been scheduled',
      });
    } catch (e) {
      console.error(e);
    }
  };

  private getColumns = (): ColumnProps[] => {
    return [
      {
        field: 'username',
        header: 'Username',
        className: 'w-1/6',
        body: (record: CaseEventRecord) => {
          const { usernameToFullName } = this.state;
          return (
            <div className={'flex items-start text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={usernameToFullName[record.username] ?? record.username} />
            </div>
          );
        },
      },
      {
        field: 'type',
        header: 'Type',
        className: 'w-1/6',
        body: (record: CaseEventRecord) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={CaseEventTextUtil.getTypeText(record)} />
            </div>
          );
        },
      },
      {
        field: 'detail',
        header: 'Detail',
        className: 'w-1/6',
        body: (record: CaseEventRecord) => {
          let text = '';
          const { userIdToFullName, usernameToFullName } = this.state;
          switch (record.type) {
            case 'CLOSE':
              text = record.reason ?? '';
              break;
            case 'COMMENT':
              text = record.content ?? '';
              break;
            case 'COMMENT_EDIT':
              text = record.content ?? '';
              break;
            case 'ASSIGNEE': {
              const assigneeKey: string = record.assignee ?? '';
              text = userIdToFullName[assigneeKey] ?? usernameToFullName[assigneeKey] ?? assigneeKey;
              break;
            }
            case 'HOLD':
              text = record.code ?? record.reason ?? 'ON_HOLD';
              break;
            case 'ACTION':
              text = CaseEventTextUtil.getRunActionText(record.action, record.success);
              break;
            case 'LIST_ADD':
            case 'LIST_REMOVE':
              text = CaseEventTextUtil.getListUpdateText(record.type, record.list, record.value);
              break;
            default:
              text = 'In Review';
          }
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={text} />
            </div>
          );
        },
      },
      {
        field: 'protocol',
        header: LanguageUtil.get({ id: 'common.label.protocol', defaultMessage: 'Protocol' }),
        className: 'w-1/6',
        body: (record: CaseEventRecord) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={record.protocol ?? '-'} />
            </div>
          );
        },
      },
      {
        field: 'port',
        header: LanguageUtil.get({ id: 'common.label.port', defaultMessage: 'Port' }),
        className: 'w-1/6',
        body: (record: CaseEventRecord) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={record.port?.toString() ?? '-'} />
            </div>
          );
        },
      },
      {
        field: 'timestamp',
        header: 'Timestamp',
        className: 'w-1/6',
        body: (record: CaseEventRecord) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={moment(record.timestamp).format('LLLL').valueOf()} />
            </div>
          );
        },
      },
    ];
  };
}

interface CaseEventRecord extends CaseEventLog {
  reason?: string;
  content?: string;
  assignee?: string;
  code?: string;
  action?: string;
  success?: boolean;
  list?: string;
  value?: string;
  closeType?: string;
  bulk?: boolean;
}


interface Props {
  transaction: TransactionData | undefined;
}

class State {
  logs: CaseEventRecord[] = [];
  loading: boolean = true;
  rows = 10;
  totalRecord = 0;
  currentPage = 1;
  usernameToFullName: Record<string, string> = {};
  userIdToFullName: Record<string, string> = {};
}

export default CaseAuditComponent;